import { t } from 'i18next'
import React, { useEffect, useRef, useState } from 'react'
import { InformationWrapper, PrevievWrapper } from './style'
import { Button } from 'semantic-ui-react'
import notify from '~/components/Notify'
import { LoginPopupsService } from '~/api/services/loginPopups'
import Layout from '~/components/Layout'
import { RouteTypes } from '~/routes/constants'
import dayjs from 'dayjs'
import { useNavigate } from 'react-router-dom'
import Dialog from '~/components/Dialog'
import { googleDocsTypes, imageTypes, videoTypes } from '~/Constants'
import { RequestCancellation } from '~/components/RequestCancellation'
import TableComponent from '~/components/TableComponent'
import { LoginPopupsQuery } from '~/QueryHooks/LoginPopupsQuery'
import i18n from '~/components/i18n'
import { handleErrors } from '~/utils/helpers'

type Props = {
  abortSignal: AbortSignal;
};

const getDefaultTableColumnHeaders = () => [
  {
    name: i18n.t('Name'),
    key: 'name'
  },
  {
    name: i18n.t('Date from'),
    key: 'from',
    type: 'date',
    format: 'DD.MM.YYYY HH:mm',
    sorted: 'desc'
  },
  {
    name: i18n.t('Date to'),
    key: 'to',
    type: 'date',
    format: 'DD.MM.YYYY HH:mm',
    sorted: 'desc'
  },
  {
    name: i18n.t('Status'),
    key: 'status',
    type: 'verified'
  },
  {
    name: i18n.t('Created at'),
    key: 'createdAt',
    type: 'date',
    format: 'DD.MM.YYYY HH:mm',
    sorted: 'desc'
  },
  {
    name: i18n.t('Actions')
  }
]

const tableActions = [
  {
    popupMessage: i18n.t('Preview'),
    type: 'icon',
    name: 'eye',
    size: 'large',
    actionType: 'preview'
  },
  {
    popupMessage: i18n.t('Edit'),
    type: 'icon',
    name: 'edit',
    size: 'large',
    actionType: 'edit',
    link: RouteTypes.SETTINGS_LOGIN_POPUP_EDIT
  },
  {
    popupMessage: i18n.t('Delete'),
    type: 'icon',
    name: 'trash',
    size: 'large',
    actionType: 'delete'
  }
]

const LoginPopup: React.FunctionComponent<Props> = ({ abortSignal }) => {
  const [deletePopupId, setDeletePopupId] = useState(null)
  const [loadingFile, setLoadingFile] = useState(false)
  const navigate = useNavigate()
  const [fileSource, setFileSource] = useState(null)
  const [isBusy, setIsBusy] = useState(false)
  const refIframe = useRef(null)
  const [tableColumnHeaders, setTableColumnHeaders] = useState(getDefaultTableColumnHeaders())
  const [dataFiltered, setDataFiltered] = useState({
    page: 0,
    rows: 20,
    column: '',
    sort: ''
  })

  const {
    isLoading: isBusyData,
    data: loginPopupsData,
    refetch: refetchLoginPopups,
    isFetching: isFetchingData
  } = LoginPopupsQuery.getLoginPopupsList(dataFiltered)

  useEffect(() => {
    setTableColumnHeaders(getDefaultTableColumnHeaders())
  }, [t])

  useEffect(() => {
    refetchLoginPopups()
  }, [dataFiltered])

  async function fetchPreviewData(id) {
    setLoadingFile(true)
    setFileSource({ src: true })
    try {
      let response
      const showResponse = await LoginPopupsService.showLoginPopup(id)
      if (showResponse.data?.content) {
        response = await LoginPopupsService.getFileUrl(id, { fileName: showResponse?.data?.content })
      }
      setFileSource({
        type: showResponse.data?.content?.split('.')[1],
        ...showResponse.data,
        src: response?.data || true
      })
    } catch (error) {
      handleErrors(error)
    }
    setLoadingFile(false)
  }

  async function handleDeletePopup() {
    setIsBusy(true)
    try {
      await LoginPopupsService.deleteLoginPopup(deletePopupId)
      setDeletePopupId(null)
      notify(t('Successfully deleted login popup'), 'success')
      refetchLoginPopups()
    } catch (error) {
      if (error.response?.data) {
        notify(error.response.data.message, 'error')
      } else {
        notify(t('General error'), 'error')
      }
    }
    setIsBusy(false)
  }

  function handleClickAction(row, type) {
    if (type === 'preview') {
      fetchPreviewData(row.id)
    } else if (type === 'edit') {
      navigate(RouteTypes.SETTINGS_LOGIN_POPUP_EDIT.replace(':id', row.id))
    } else if (type === 'delete') {
      setDeletePopupId(row.id)
    }
  }

  function renderTable() {
    const { filtered, rows } = loginPopupsData ? loginPopupsData : { filtered: 0, rows: [] }
    return <TableComponent
      isFetchingData={isFetchingData}
      animated
      tableColumnHeaders={tableColumnHeaders}
      dataFiltered={dataFiltered}
      handleChangeDataFiltered={(data, headers) => {
        setTableColumnHeaders(headers)
        setDataFiltered(data)
      }}
      actions={tableActions}
      handleClickAction={handleClickAction}
      rows={rows}
      total={filtered}
    />
  }

  function renderDeleteModal() {
    return <Dialog
      open={Boolean(deletePopupId)}
      handleClose={() => setDeletePopupId(null)}
      textHeader={t('Delete popup')}
      textContent={t('Do you really want to delete popup?')}
      textClose={t('Close')}
      isLoading={isBusy}
      textSubmit={t('Delete')}
      handleSubmit={() => handleDeletePopup()}
      type={'abort'}
    />
  }

  function renderPreviewInformation() {
    return <>
      <div style={{ textAlign: 'initial' }}>
        <div className='label'>{t('Created at:')}</div>
        <div className='value'>{dayjs(fileSource?.createdAt).format('DD.MM.YYYY HH:mm')}</div>
      </div>
      <div style={{ textAlign: 'initial' }}>
        <div className='label'>{t('File type:')}</div>
        <div className='value'>{fileSource?.type}</div>
      </div>
      <div style={{ textAlign: 'initial' }}>
        <div className='label'>{t('Date from:')}</div>
        <div className='value'>{dayjs(fileSource?.from).format('DD.MM.YYYY HH:mm')}</div>
      </div>
      <div style={{ textAlign: 'initial' }}>
        <div className='label'>{t('Date to:')}</div>
        <div className='value'>{dayjs(fileSource?.to).format('DD.MM.YYYY HH:mm')}</div>
      </div>
    </>
  }

  function handleRefreshClick() {
    if (refIframe.current) {
      refIframe.current.setAttribute('src', '')
      const encodedUrl = encodeURIComponent(fileSource?.src)
      const embeddedUrl = `https://docs.google.com/gview?url=${encodedUrl}&embedded=true`
      refIframe.current.setAttribute('src', embeddedUrl)
    }
  }

  function renderModalPreviewFile() {
    const title = fileSource?.title
    const fileTypeLower = fileSource?.type?.toLowerCase()
    const googleDocsType = googleDocsTypes.find(type => type?.toLocaleLowerCase() === fileTypeLower?.toLocaleLowerCase())

    return (
      <Dialog
        open={Boolean(fileSource?.src)}
        handleClose={() => setFileSource(null)}
        textContent={<div style={{ position: 'relative', width: '100%', height: '100%' }}>
          <div style={{ margin: 20 }} dangerouslySetInnerHTML={{ __html: fileSource?.headerText }} />
          {fileSource?.type && <>
            <InformationWrapper>
              {renderPreviewInformation()}
              <Button type={'button'}
                onClick={() => handleRefreshClick()}
                basic
                icon={'refresh'}
                content={t('Refresh')} />
            </InformationWrapper>
            <PrevievWrapper>
              {(imageTypes.find(imageType => imageType === fileTypeLower)) && <img src={fileSource?.src as string} alt='Preview' />}
            </PrevievWrapper>
            <PrevievWrapper>
              {videoTypes.find(videoType => videoType === fileTypeLower) && <video src={fileSource?.src as string} controls />}
            </PrevievWrapper>
            {googleDocsType && (
              <iframe
                ref={refIframe}
                src={`https://docs.google.com/viewer?url=${encodeURIComponent(fileSource?.src)}&embedded=true`}
                width='100%'
                height='80%'
              />
            )}
          </>}
          <div style={{ margin: 20 }} dangerouslySetInnerHTML={{ __html: fileSource?.footerText }} />
        </div>}
        textHeader={t('Preview: {{title}}', { title })}
        textClose={t('Close')}
        type={'preview'}
        isLoading={loadingFile}
      />
    )
  }

  return <Layout
    title={t('Login popups')}
    isBusy={isBusyData}
    segmented
    actionName={t('Create login popup')}
    action={RouteTypes.SETTINGS_LOGIN_POPUP_CREATE}
  >
    {renderTable()}
    {renderModalPreviewFile()}
    {renderDeleteModal()}
  </Layout>
}

export default RequestCancellation(LoginPopup)
